import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import { AiFillCaretRight } from "react-icons/ai";
import {
  HiOutlineShoppingCart,
  HiOutlineLightningBolt,
  HiOutlineGlobeAlt,
  HiOutlineShieldCheck,
  HiOutlineClock,
  HiOutlineTruck,
  HiOutlineScale,
  HiOutlineClipboardList,
  HiOutlineSupport,
} from "react-icons/hi";
import { scrollTop } from "../../../utils/window";
import BrandLogo from "../../../components/BrandLogo/BrandLogo";
import { resolvePortalIdentity } from "../../utils/portalIdentity";
import "./dealers-home.css";

const HERO = "/images/dealers-hero-main.jpg?v=1";
const HERO_SPLIT = "/images/dealers-hero-split.jpg?v=1";
const LANE_BUY = "/images/dealers-lane-buynow.jpg?v=3";
const LANE_AUCTION = "/images/dealers-lane-auction.jpg?v=3";
const LANE_SOURCE = "/images/dealers-lane-sourcing.jpg?v=3";
const BENEFITS_BG = "/images/dealers-benefits-ops.jpg?v=1";

function DealersHome({ auth }) {
  const [identityTick, setIdentityTick] = useState(0);

  useEffect(() => {
    scrollTop();
  }, []);

  useEffect(() => {
    const onStorage = (event) => {
      if (
        !event.key ||
        event.key.includes("localstorage_auth") ||
        event.key === "ATP_localstorage_auth" ||
        event.key === "ATP_DEALERS_localstorage_auth"
      ) {
        setIdentityTick((n) => n + 1);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const identity = useMemo(
    () => resolvePortalIdentity(auth),
    // identityTick forces client-session re-read after same-tab changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [auth, identityTick]
  );
  const displayName = identity.displayName || "Dealer";
  const roleLabel = identity.roleLabel || "Dealer";

  const lanes = [
    {
      id: "buy",
      step: "01",
      eyebrow: "Instant stock",
      title: "Buy now",
      copy: "Browse certified wholesale inventory priced to move — clear condition, fixed dealer pricing, and paperwork ready for a clean handoff.",
      outcome: "Best when you need stock on the lot today.",
      points: [
        "Certified wholesale inventory",
        "Fixed pricing — no bid wait",
        "Condition & docs visible upfront",
        "Ready to ship or collect",
      ],
      cta: "Open buy now",
      to: "/dealers/dashbord/buynow",
      image: LANE_BUY,
      align: "left",
      Icon: HiOutlineShoppingCart,
    },
    {
      id: "auction",
      step: "02",
      eyebrow: "Live floor",
      title: "Auctions",
      copy: "Compete on timed and live lots with transparent increments, watchlists, and dealer-grade controls built for a busy trade day.",
      outcome: "Best when you want competitive wholesale pricing.",
      points: [
        "Timed and live auction lanes",
        "Transparent bid increments",
        "Watchlists for the lots you want",
        "Dealer bidding & settlement tools",
      ],
      cta: "Enter auctions",
      to: "/dealers/dashbord/auction",
      image: LANE_AUCTION,
      align: "right",
      Icon: HiOutlineLightningBolt,
    },
    {
      id: "source",
      step: "03",
      eyebrow: "Sourcing desk",
      title: "Show interest",
      copy: "Tell us the make, model, and volume you need — we route demand across the AutoTrade Port network and match supply fast.",
      outcome: "Best when the stock you need is not on the floor yet.",
      points: [
        "Send exact stock requirements",
        "Network-wide demand routing",
        "Supply match across dealers",
        "Priority follow-up from the desk",
      ],
      cta: "Request stock",
      to: "/dealers/showInterest",
      image: LANE_SOURCE,
      align: "left",
      Icon: HiOutlineGlobeAlt,
    },
  ];

  const benefits = [
    {
      step: "01",
      Icon: HiOutlineShieldCheck,
      title: "Verified access",
      copy: "Dealer accounts with secure bidding, settlement tools, and role-based floor controls.",
      points: ["Authenticated dealer login", "Protected bid & settlement flow"],
    },
    {
      step: "02",
      Icon: HiOutlineClock,
      title: "Faster turns",
      copy: "Move from lot to purchase without the usual wholesale friction — clear condition, clear next step.",
      points: ["Buy now + auction lanes", "Less back-and-forth on stock"],
    },
    {
      step: "03",
      Icon: HiOutlineTruck,
      title: "Ops ready",
      copy: "Shipping, arbitration, and watchlists sit inside the same workflow your team already uses.",
      points: ["Watchlists & follow-ups", "Shipping and dispute tools"],
    },
    {
      step: "04",
      Icon: HiOutlineScale,
      title: "Transparent floor",
      copy: "Timed and live lots with visible increments so every bid reads clean on a busy trade day.",
      points: ["Live & timed auctions", "Clear bid increments"],
    },
    {
      step: "05",
      Icon: HiOutlineClipboardList,
      title: "Dealer paperwork",
      copy: "Condition and paperwork expectations stay visible — so stock moves with fewer surprises.",
      points: ["Condition clarity", "Dealer-ready documentation"],
    },
    {
      step: "06",
      Icon: HiOutlineSupport,
      title: "Network desk",
      copy: "Show interest and route demand across AutoTrade Port when the lot you need is not on the floor yet.",
      points: ["Demand routing", "Priority follow-up"],
    },
  ];

  return (
    <div className="dh-page">
      <section className="dh-hero" aria-label="Dealers home">
        <div className="dh-hero__media" aria-hidden="true">
          <div
            className="dh-hero__photo"
            style={{ backgroundImage: `url(${HERO})` }}
          />
          <div
            className="dh-hero__photo dh-hero__photo--split"
            style={{ backgroundImage: `url(${HERO_SPLIT})` }}
          />
          <div className="dh-hero__shade" />
          <div className="dh-hero__vignette" />
          <div className="dh-hero__orb dh-hero__orb--a" />
          <div className="dh-hero__orb dh-hero__orb--b" />
          <div className="dh-hero__grain" />
          <div className="dh-hero__beam" />
          <div className="dh-hero__scan" />
          <div className="dh-hero__edge" />
        </div>

        <div className="dh-hero__shell">
          <div className="dh-hero__content">
            <div className="dh-hero__brand-row">
              <BrandLogo
                variant="light"
                compact
                className="dh-hero__brand"
              />
              <span className="dh-hero__dealers-pill">Dealers</span>
            </div>
            <p className="dh-hero__eyebrow">
              <span className="dh-hero__live" aria-hidden="true" />
              Live wholesale · Auctions · Network
            </p>
            <h1 className="dh-hero__title">
              The dealer trade floor,
              <span className="dh-hero__title-accent">built for speed.</span>
            </h1>
            <p className="dh-hero__lede">
              Source certified stock, bid live, and move inventory across the
              AutoTrade Port dealer network.
            </p>
            <div className="dh-hero__ctas">
              <Link to="/dealers/dashbord" className="dh-btn dh-btn--primary">
                Open dashboard
                <AiFillCaretRight size={16} aria-hidden />
              </Link>
              <Link to="/dealers/dashbord/auction" className="dh-btn dh-btn--ghost">
                Enter auctions
                <AiFillCaretRight size={16} aria-hidden />
              </Link>
              <Link to="/dealers/dashbord/buynow" className="dh-btn dh-btn--ghost">
                Browse buy now
                <AiFillCaretRight size={16} aria-hidden />
              </Link>
            </div>
            <ul className="dh-hero__signals">
              <li>
                {roleLabel}: {displayName}
              </li>
              <li>Timed &amp; live lots</li>
              <li>Network sourcing</li>
            </ul>
          </div>

          <aside className="dh-hero__rail" aria-label="Trade floor status">
            <div className="dh-hero__rail-head">
              <span className="dh-hero__rail-mark" aria-hidden="true" />
              <p className="dh-hero__rail-kicker">Trade floor</p>
              <p className="dh-hero__rail-status">
                <span className="dh-hero__live" aria-hidden="true" />
                Ready for you
              </p>
            </div>
            <ul className="dh-hero__rail-list">
              <li>
                <span className="dh-hero__rail-num">01</span>
                <div>
                  <p className="dh-hero__rail-line">Auctions</p>
                  <p className="dh-hero__rail-meta">Timed &amp; live lots</p>
                </div>
              </li>
              <li>
                <span className="dh-hero__rail-num">02</span>
                <div>
                  <p className="dh-hero__rail-line">Buy now</p>
                  <p className="dh-hero__rail-meta">Certified wholesale stock</p>
                </div>
              </li>
              <li>
                <span className="dh-hero__rail-num">03</span>
                <div>
                  <p className="dh-hero__rail-line">Sourcing desk</p>
                  <p className="dh-hero__rail-meta">Network demand match</p>
                </div>
              </li>
            </ul>
            <Link to="/dealers/dashbord" className="dh-hero__rail-cta">
              Go to dashboard
              <AiFillCaretRight size={14} aria-hidden />
            </Link>
          </aside>
        </div>

        <div className="dh-hero__bottom" aria-hidden="true">
          <span className="dh-hero__scroll-hint">Scroll the floor</span>
          <span className="dh-hero__scroll-line" />
        </div>
      </section>

      <section className="dh-pathways" aria-labelledby="dh-pathways-heading">
        <div className="dh-pathways__intro">
          <div>
            <p className="dh-pathways__label">Dealer pathways</p>
            <h2 id="dh-pathways-heading">Three ways to move stock.</h2>
          </div>
          <p>
            Pick a lane that matches how you trade — buy certified stock
            instantly, bid on the live floor, or send sourcing demand into the
            AutoTrade Port network.
          </p>
        </div>

        <div className="dh-lanes">
          {lanes.map((lane) => {
            const Icon = lane.Icon;
            return (
              <article
                key={lane.id}
                className={`dh-lane dh-lane--${lane.align}`}
              >
                <div
                  className="dh-lane__media"
                  style={{ backgroundImage: `url(${lane.image})` }}
                  aria-hidden="true"
                />
                <div className="dh-lane__shade" aria-hidden="true" />
                <div className="dh-lane__frame" aria-hidden="true" />
                <div className="dh-lane__body">
                  <div className="dh-lane__meta">
                    <span className="dh-lane__icon" aria-hidden="true">
                      <Icon size={20} />
                    </span>
                    <span className="dh-lane__step">{lane.step}</span>
                  </div>
                  <p className="dh-lane__eyebrow">{lane.eyebrow}</p>
                  <h3 className="dh-lane__title">{lane.title}</h3>
                  <p className="dh-lane__copy">{lane.copy}</p>
                  <p className="dh-lane__outcome">{lane.outcome}</p>
                  <ul className="dh-lane__points">
                    {lane.points.map((point) => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                  <Link to={lane.to} className="dh-btn dh-btn--lane">
                    {lane.cta}
                    <AiFillCaretRight size={16} aria-hidden />
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section
        className="dh-benefits"
        aria-labelledby="dh-benefits-heading"
        style={{
          "--dh-benefits-bg": `url(${BENEFITS_BG})`,
        }}
      >
        <div className="dh-benefits__media" aria-hidden="true">
          <div className="dh-benefits__photo" />
          <div className="dh-benefits__shade" />
          <div className="dh-benefits__glow" />
        </div>
        <div className="dh-benefits__inner">
          <header className="dh-benefits__head">
            <p className="dh-pathways__label">Why dealers stay</p>
            <h2 id="dh-benefits-heading">Built for the wholesale day.</h2>
            <p className="dh-benefits__lede">
              One verified floor for buy now, auctions, and sourcing — with the
              ops tools that keep turns moving.
            </p>
            <ul className="dh-benefits__proof" aria-label="Dealer advantages">
              <li>Verified access</li>
              <li>Faster lot-to-purchase</li>
              <li>Watchlists · shipping · arbitration</li>
            </ul>
          </header>

          <div className="dh-benefits__grid">
            {benefits.map(({ step, Icon, title, copy, points }) => (
              <article key={title} className="dh-benefit">
                <div className="dh-benefit__meta">
                  <span className="dh-benefit__icon" aria-hidden="true">
                    <Icon size={22} />
                  </span>
                  <span className="dh-benefit__step">{step}</span>
                </div>
                <h3>{title}</h3>
                <p>{copy}</p>
                <ul className="dh-benefit__points">
                  {points.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section
        className="dh-close"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(7, 11, 20, 0.72), rgba(15, 23, 42, 0.78)), url(${LANE_SOURCE})`,
        }}
      >
        <div className="dh-close__panel">
          <p className="dh-pathways__label dh-pathways__label--on-dark">
            Next step
          </p>
          <h2>Ready when you are, {displayName}.</h2>
          <p>
            Jump back into auctions, buy now stock, or your dealer dashboard.
          </p>
          <div className="dh-close__ctas">
            <Link to="/dealers/dashbord" className="dh-btn dh-btn--primary">
              Open dashboard
              <AiFillCaretRight size={16} aria-hidden />
            </Link>
            <Link to="/dealers/dashbord/auction" className="dh-btn dh-btn--ghost">
              View auctions
            </Link>
            <Link to="/dealers/dashbord/buynow" className="dh-btn dh-btn--ghost">
              Browse buy now
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default connect((state) => ({ auth: state.auth }))(DealersHome);
